import type { IDefaultFilter } from 'src/pages/tickets/tickets.types';

import { useState, useEffect } from 'react';

import { defaultFilter } from 'src/pages/tickets/tickets.types';
import { useFilterLifecycleMutation } from 'src/pages/setting/components/lifecycle/slice/lifecycleApi';

export interface IsetQueryParamsHandler {
  type: 'search' | 'pagination' | 'reset';
  value?: string;
}

export const useGetLifecycleStages = () => {
  const updatedFilter = {
    ...defaultFilter,
    filters: [...defaultFilter.filters],
  };

  const [getListLifecycle] = useFilterLifecycleMutation();

  const initialPaginationVal = { fetched: 0, total: Infinity };

  const [_recordList, setRecordList] = useState<any>([]);
  const [lifecyclePagination, setLifecyclePagination] = useState(initialPaginationVal);
  const [queryParams, setQueryParams] = useState<IDefaultFilter>(updatedFilter);
  const [isFetching, setIsFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const openListHandler = () => setOpen(true);

  const closeListHandler = () => {
    setOpen(false);
    queryParamsHandler({ type: 'reset' });
  };

  const resetPagination = () => {
    setLifecyclePagination(initialPaginationVal);
  };

  // -------- Query Params Handler ----------
  const queryParamsHandler = ({ type, value }: IsetQueryParamsHandler) => {
    switch (type) {
      case 'search': {
        setQueryParams((prev) => {
          if (prev.globalSearch !== value) {
            resetPagination();
            return { ...prev, page: 1, globalSearch: value || '' };
          }
          return prev;
        });
        break;
      }

      case 'pagination':
        setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }));
        break;

      case 'reset':
        setQueryParams(updatedFilter);
        break;

      default:
        break;
    }
  };

  // ---------- Infinite Scroll Handler ----------
  const listScrollHandler = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const isBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;

    const hasMore = lifecyclePagination.fetched < lifecyclePagination.total;

    if (isBottom && !isFetching && hasMore) {
      queryParamsHandler({ type: 'pagination' });
    }
  };

  // -------- Load Lifecycle List ----------
  const getLifecycleListFunc = async (params: IDefaultFilter) => {
    try {
      setIsFetching(true);

      const lifecycleData: any = await getListLifecycle(params).unwrap();

      // Extract data from the response structure
      // const lifecycleData = res?.data?.lifecycleStage;
      const list = lifecycleData?.list || [];
      const total = list.length; // Or use total from API if available

      setLifecyclePagination((prev) => ({
        fetched: prev.fetched + list.length,
        total,
      }));

      setRecordList((prev: any) => {
        const newList = params.page === 1 || params.globalSearch ? list : [...prev, ...list];

        // Remove duplicates by id
        const seen = new Set();
        return newList.filter((item: any) => {
          const isDup = seen.has(item.id);
          seen.add(item.id);
          return !isDup;
        });
      });
    } catch (error) {
      console.error('Error fetching lifecycle list:', error);
    } finally {
      setIsFetching(false);
    }
  };

  // -------- UseEffect - list load trigger ----------
  useEffect(() => {
    if (open) {
      getLifecycleListFunc(queryParams);
    } else {
      setRecordList([]);
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryParams]);

  // -------- Debounced Search ----------
  useEffect(() => {
    const timeout = setTimeout(() => {
      queryParamsHandler({ type: 'search', value: searchValue });
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return {
    _recordList,
    isFetching,
    searchValue,
    open,
    resetPagination,
    queryParamsHandler,
    listScrollHandler,
    searchValueHandler: setSearchValue,
    openListHandler,
    closeListHandler,
  };
};
